import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Image as ImageIcon, Star } from "lucide-react";
import { api, absUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo, formatPrice } from "@/lib/constants";
import { toast } from "sonner";
import RateSellerDialog from "@/components/RateSellerDialog";
import ReportButton from "@/components/ReportButton";

export default function Conversation() {
    const { id } = useParams();
    const [conv, setConv] = useState(null);
    const [body, setBody] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [canRate, setCanRate] = useState({ can_rate: false, already_rated: false });
    const [rateOpen, setRateOpen] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const bottomRef = useRef(null);
    const fileRef = useRef(null);

    async function load() {
        try {
            const { data } = await api.get(`/messages/conversations/${id}`);
            setConv(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function loadCanRate() {
        try {
            const { data } = await api.get(`/messages/conversations/${id}/can-rate`);
            setCanRate(data);
        } catch {}
    }

    useEffect(() => {
        load();
        loadCanRate();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [conv?.messages?.length]);

    async function uploadImage(file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data.url;
    }

    async function send() {
        if (!body.trim() && !imageUrl) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/messages/conversations/${id}/messages`, {
                body: body.trim(),
                image_url: imageUrl || null,
            });
            setConv((c) => ({ ...c, messages: [...(c?.messages || []), data] }));
            setBody("");
            setImageUrl("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (!conv) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-onyx-900">
                <div className="w-10 h-10 border-2 border-onyx-600 border-t-hellcat rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onyx-900 flex flex-col">
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="w-9 h-9 rounded-full surface-elevated overflow-hidden">
                        {conv.other_user?.avatar_url ? (
                            <img
                                src={absUrl(conv.other_user.avatar_url)}
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center label-caps text-onyx-500">
                                {conv.other_user?.name?.[0] || "?"}
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <Link
                            to={`/users/${conv.other_user?.user_id}`}
                            className="headline text-base truncate block hover:text-hellcat"
                        >
                            {conv.other_user?.name}
                        </Link>
                        {conv.listing && (
                            <div className="text-[11px] text-onyx-500 truncate">
                                Re: {conv.listing.year} {conv.listing.model_name} · {formatPrice(conv.listing.price)}
                            </div>
                        )}
                    </div>
                    {conv.other_user && (
                        <div className="flex items-center gap-1.5">
                            {canRate.can_rate && (
                                <button
                                    onClick={() => setRateOpen(true)}
                                    data-testid="conv-rate-btn"
                                    className="btn-red rounded-sm px-2.5 py-1.5 text-xs inline-flex items-center gap-1"
                                >
                                    <Star className="w-3.5 h-3.5" /> Rate
                                </button>
                            )}
                            {canRate.already_rated && (
                                <span className="label-caps text-[10px] text-onyx-500 inline-flex items-center gap-1">
                                    <Star className="w-3 h-3 fill-hellcat text-hellcat" /> Rated
                                </span>
                            )}
                            <ReportButton
                                targetType="user"
                                targetId={conv.other_user.user_id}
                                testid="report-user-btn"
                                className="p-1"
                            />
                        </div>
                    )}
                </div>
            </div>

            {conv.listing && (
                <Link
                    to={`/listings/${conv.listing.listing_id}`}
                    className="max-w-3xl mx-auto w-full px-4 pt-3"
                >
                    <div className="surface rounded-md p-3 flex items-center gap-3">
                        {conv.listing.photos?.[0] && (
                            <img
                                src={absUrl(conv.listing.photos[0])}
                                className="w-16 h-16 rounded-sm object-cover"
                                alt=""
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="headline text-sm truncate">
                                {conv.listing.year} {conv.listing.model_name} {conv.listing.trim}
                            </div>
                            <div className="text-hellcat font-bold text-sm">
                                {formatPrice(conv.listing.price)}
                            </div>
                        </div>
                    </div>
                </Link>
            )}

            <div className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-4 py-4 space-y-3">
                {conv.messages.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    return (
                        <div
                            key={m.message_id}
                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        >
                            <div
                                className={`max-w-[80%] rounded-md px-3 py-2 text-sm ${
                                    mine
                                        ? "bg-hellcat text-white"
                                        : "surface text-white"
                                }`}
                                data-testid={`msg-${m.message_id}`}
                            >
                                {m.image_url && (
                                    <img
                                        src={absUrl(m.image_url)}
                                        alt=""
                                        className="rounded-sm mb-2 max-w-[240px]"
                                    />
                                )}
                                {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                                <div
                                    className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-onyx-500"}`}
                                >
                                    {timeAgo(m.created_at)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            <div
                className="sticky bottom-0 border-t border-onyx-600 backdrop-blur-xl safe-bottom"
                style={{ background: "rgba(10,10,10,0.9)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3">
                    {imageUrl && (
                        <div className="mb-2 flex items-center gap-2">
                            <img
                                src={absUrl(imageUrl)}
                                alt=""
                                className="w-14 h-14 rounded-sm object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => setImageUrl("")}
                                className="text-xs text-hellcat"
                            >
                                Remove
                            </button>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                try {
                                    const url = await uploadImage(f);
                                    setImageUrl(url);
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                }
                            }}
                            data-testid="msg-image-input"
                        />
                        <button
                            onClick={() => fileRef.current?.click()}
                            className="btn-ghost rounded-sm px-3 py-2.5"
                            data-testid="msg-image-btn"
                        >
                            <ImageIcon className="w-4 h-4" />
                        </button>
                        <input
                            data-testid="msg-input"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            className="flex-1 bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-hellcat"
                            placeholder="Type a message"
                        />
                        <button
                            onClick={send}
                            disabled={busy}
                            data-testid="msg-send"
                            className="btn-red rounded-sm px-3 py-2.5"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {conv.other_user && (
                <RateSellerDialog
                    open={rateOpen}
                    onOpenChange={setRateOpen}
                    userId={conv.other_user.user_id}
                    userName={conv.other_user.name}
                    listingId={conv.listing_id}
                    conversationId={conv.conversation_id}
                    onRated={loadCanRate}
                />
            )}
        </div>
    );
}
